import styled from "styled-components";
import { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Wrapper,
  ProgressBar,
  ProgressFill,
  ProgressWrap,
  OtpFlowTitle,
  ProgressLabel,
  OtpFlowSubText
} from "../../common-styles";
import {
  selectLoading,
  showEscalation,
  selectOtpError,
  loadOtpSession,
  selectResendAvailableAt,
  selectVerifyOtpLimitReached
} from "../../store/slices/otpSlice";
import { OTP_LOADING_KEYS } from "../../templates/strings";
import { verifyOtpThunk, resendOtpThunk } from "../../store/thunks/otpThunks";

const EditIcon = styled.span`
  cursor: pointer;
  font-size: 14px;
`;
const DigitRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 18px;
`;
const DigitBox = styled.input`
  width: clamp(42px, 12vw, 52px);
  height: clamp(50px, 14vw, 62px);
  border: 2px solid ${({ $filled }) => ($filled ? "#333" : "#ccc")};
  border-radius: clamp(4px, 1vw, 6px);
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  outline: none;
  transition: border-color 0.15s ease;
  &:focus {
    border-color: #333;
    outline: none;
  }
  @media (max-width: 481px) {
    width: 40px;
    height: 50px;
    font-size: 20px;
  }
  @media (max-width: 349px) {
    width: 30px;
    height: 40px;
    font-size: 18px;
  }
`;
const ErrorText = styled.p`
  color: #c0392b;
  font-size: 13px;
  text-align: center;
  margin-bottom: 8px;
`;
const ResendRow = styled.p`
  text-align: center;
  font-size: 14px;
  color: #333;
  margin-bottom: 16px;
`;
const ResendLink = styled.span`
  color: #e94e1b;
  text-decoration: underline;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
`;
const ConfirmBtn = styled.button`
  width: 100%;
  padding: 14px 0 10px;
  border: none;
  border-radius: 6px;
  background: ${({ $active }) => ($active ? "#e15d3a" : "#d0d0d0")};
  color: #fff;
  cursor: ${({ $active }) => ($active ? "pointer" : "not-allowed")};
  transition: background 0.2s;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;
const BtnMain = styled.span`
  font-size: 18px;
  font-weight: 700;
`;
const BtnSub = styled.span`
  font-size: 12px;
  opacity: 0.85;
`;
const EscalationLink = styled.span`
  display: block;
  text-align: center;
  font-size: 14px;
  color: #555;
  text-decoration: underline;
  cursor: pointer;
`;

const CODE_LENGTH = 6;
// Always a fresh array: a shared one got mutated by paste and its stale code
// was verified again on the next mount
const emptyDigits = () => Array(CODE_LENGTH).fill("");

const OtpPage = ({ leadId }) => {
  const dispatch = useDispatch();
  const inputRefs = useRef([]);
  const [digits, setDigits] = useState(emptyDigits);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [otpSession] = useState(() => loadOtpSession());

  const funnelInfo = useSelector((state) => state.funnelInfo);
  const phone = funnelInfo.redirectUrlParams.phone || otpSession?.phone;
  const verifyOtpLimitReached = useSelector(selectVerifyOtpLimitReached);
  const otpError = useSelector(selectOtpError);
  const resendAvailableAt = useSelector(selectResendAvailableAt);
  const isVerifying = useSelector(selectLoading(OTP_LOADING_KEYS.VERIFY_OTP));
  const isResending = useSelector(selectLoading("resendOtp"));

  const isComplete = digits.every((d) => d !== "");

  // ── Resend countdown
  useEffect(() => {
    if (!resendAvailableAt) return;
    const tick = () => {
      const ms = new Date(resendAvailableAt).getTime() - Date.now();
      if (ms <= 0) {
        setSecondsLeft(0);
        setCanResend(true);
      } else {
        setSecondsLeft(Math.ceil(ms / 1000));
        setCanResend(false);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [resendAvailableAt]);

  // ── Auto-verify when 6th digit entered
  useEffect(() => {
    if (isComplete) {
      dispatch(verifyOtpThunk({ leadId, code: digits.join("") }));
    }
  }, [digits]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Digit input handlers
  // Writes `code` into the boxes from `start` on and focuses the box after it
  const fillDigits = (base, start, code) => {
    const next = [...base];
    code
      .slice(0, CODE_LENGTH - start)
      .split("")
      .forEach((digit, offset) => {
        next[start + offset] = digit;
      });
    setDigits(next);
    inputRefs.current[Math.min(start + code.length, CODE_LENGTH - 1)]?.focus();
  };

  const handleChange = (i, val) => {
    let clean = val.replace(/\D/g, "");
    // A box that already holds a digit got a second one: keep the new digit
    if (digits[i] && clean.length === 2) clean = clean.replace(digits[i], "");
    if (clean.length >= CODE_LENGTH) {
      // The whole code at once: SMS autofill or a keyboard code suggestion
      fillDigits(emptyDigits(), 0, clean.slice(-CODE_LENGTH));
    } else if (clean.length > 1) {
      fillDigits(digits, i, clean);
    } else {
      const next = [...digits];
      next[i] = clean;
      setDigits(next);
      if (clean && i < CODE_LENGTH - 1) inputRefs.current[i + 1]?.focus();
    }
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      if (digits[i]) {
        const next = [...digits];
        next[i] = "";
        setDigits(next);
      } else if (i > 0) {
        inputRefs.current[i - 1]?.focus();
      }
    }
    if (e.key === "ArrowLeft" && i > 0) inputRefs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < CODE_LENGTH - 1)
      inputRefs.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);
    if (!pasted) return;
    fillDigits(emptyDigits(), 0, pasted);
  };

  const handleResend = () => {
    if (!canResend || isResending) return;
    setDigits(emptyDigits());
    dispatch(resendOtpThunk({ leadId }));
    inputRefs.current[0]?.focus();
  };

  const handleManualVerify = () => {
    if (!isComplete || isVerifying) return;
    dispatch(verifyOtpThunk({ leadId, code: digits.join("") }));
  };

  return (
    <Wrapper>
      <ProgressWrap>
        <ProgressBar>
          <ProgressFill $width="85" />
        </ProgressBar>
        <ProgressLabel>Fast geschafft</ProgressLabel>
      </ProgressWrap>

      <OtpFlowTitle>Bitte Bestätigungscode eingeben</OtpFlowTitle>

      {/* Phone with edit pencil */}
      <OtpFlowSubText className="mt-3 d-flex align-items-center flex-wrap">
        Wir haben dir zur Verifizierung einen Code per
        <strong className="mx-1">SMS</strong>an
        <EditIcon
          className="mx-1 text-decoration-underline"
          title="Nummer korrigieren"
          onClick={() => dispatch(showEscalation())}
        >
          <span>"{phone}"</span>
          <FontAwesomeIcon icon={faPen} className="ms-1" />
        </EditIcon>{" "}
        gesendet.
      </OtpFlowSubText>

      {/* 6 digit boxes */}
      <DigitRow onPaste={handlePaste}>
        {digits.map((d, i) => (
          <DigitBox
            key={i}
            ref={(el) => (inputRefs.current[i] = el)}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            // Lets iOS/Android offer the code from the SMS; no maxLength,
            // so the whole code can arrive in one box and gets spread out
            autoComplete="one-time-code"
            aria-label={`Ziffer ${i + 1} von ${CODE_LENGTH}`}
            value={d}
            autoFocus={i === 0}
            // Typing into a filled box replaces its digit
            onFocus={(e) => e.target.select()}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            $filled={!!d}
            $active={i === digits.findIndex((x) => x === "") && i !== 0}
          />
        ))}
      </DigitRow>

      {otpError && <ErrorText>{otpError}</ErrorText>}

      {!verifyOtpLimitReached && (
        <>
          {/* Countdown / resend */}
          <ResendRow>
            {canResend ? (
              <ResendLink onClick={handleResend} disabled={isResending}>
                {isResending ? "Wird gesendet..." : "Code erneut senden"}
              </ResendLink>
            ) : (
              <strong>Erneut senden in {secondsLeft}s</strong>
            )}
          </ResendRow>

          {/* Confirm button — grey(disabled) until all 6 entered, then orange */}
          <ConfirmBtn
            $active={isComplete}
            disabled={!isComplete || isVerifying}
            onClick={handleManualVerify}
          >
            <BtnMain>{isVerifying ? "Wird geprüft..." : "Bestätigen"}</BtnMain>
            <BtnSub>kostenfrei &amp; unverbindlich</BtnSub>
          </ConfirmBtn>

          {/* "Didn't receive code?" link */}
          <EscalationLink onClick={() => dispatch(showEscalation())}>
            Code nicht erhalten?
          </EscalationLink>
        </>
      )}
    </Wrapper>
  );
};

export default OtpPage;
